"use client";

import { useState } from "react";
import { SelectField } from "@/components/tool-kit/primitives";
import { BrogliePanel } from "@/components/tools/physics-waves/broglie-panel";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";

/** English strings. A translated content file overrides these through `ui`. */
export const DE_BROGLIE_WAVELENGTH_CALCULATOR_STRINGS = {
  sigFigs: "Round to this many significant figures",
  unitFor: "Unit for {name}",
  particleLabel: "Particle",
  particles: {
    electron: "Electron",
    muon: "Muon",
    proton: "Proton",
    neutron: "Neutron",
    alpha: "Alpha particle",
  },
  customParticle: "Another particle or object…",
  knownLabel: "What do you know?",
  knowns: {
    speed: "Speed",
    kinetic: "Kinetic energy",
    voltage: "Accelerating voltage",
    momentum: "Momentum",
  },
  massInput: "Mass",
  chargeInput: "Charge (multiples of e)",
  speedInput: "Speed",
  kineticInput: "Kinetic energy",
  voltageInput: "Accelerating voltage",
  momentumInput: "Momentum",
  methodLabel: "Method",
  methodExact: "Exact (relativistic)",
  methodClassical: "Classical (p = mv)",
  tryLabel: "Try an example",
  presets: {
    davisson: "Davisson–Germer electron (54 V)",
    microscope: "Electron microscope (100 kV)",
    neutron: "Thermal neutron (0.025 eV)",
    alpha: "Alpha particle (5 MeV)",
    c60: "C₆₀ molecule at 220 m/s",
    baseball: "Baseball at 40 m/s",
  },
  results: "Results",
  working: "The working",
  enterValue:
    "Enter a valid number above zero in every box to see the wavelength.",
  invalid: "The mass and the value must both be above zero.",
  tooFast:
    "That speed or energy is not possible: a particle with mass cannot reach the speed of light, and the classical formula would give a speed above it.",
  neutralNoVoltage:
    "A neutral particle is not accelerated by a voltage. Give its speed, kinetic energy or momentum instead.",
  wavelength: "de Broglie wavelength (λ)",
  momentum: "Momentum (p)",
  kinetic: "Kinetic energy",
  kineticJoules: "Kinetic energy in joules",
  speed: "Speed",
  beta: "Speed as a fraction of c (β)",
  gamma: "Lorentz factor (γ)",
  classicalError: "Classical formula's error",
  stepWavelength: "λ = h ÷ p = {h} ÷ {p} = {lambda}",
  fastNote:
    "Beyond about 10% of the speed of light, the classical p = mv becomes noticeably wrong; switch to the exact method.",
  scaleLabel:
    "The wavelength compared with a proton, an atom and visible light",
  scaleDescription:
    "A logarithmic length scale from 10⁻³⁶ to 10⁻³ metres with markers for a proton, an atom and visible light, and a pointer at this particle's de Broglie wavelength.",
  markProton: "Proton",
  markAtom: "Atom",
  markLight: "Visible light",
  scaleValue: "λ = {value}",
} as const;

const SIG_CHOICES = ["3", "4", "5"] as const;

export default function DeBroglieWavelengthCalculatorWidget() {
  const strings = useToolUi(DE_BROGLIE_WAVELENGTH_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [sigText, setSigText] = useState<(typeof SIG_CHOICES)[number]>("4");
  return (
    <>
      <div className="max-w-xs">
        <SelectField
          id="dbw-sig"
          label={strings.sigFigs}
          onChange={setSigText}
          options={SIG_CHOICES.map((value) => ({ value, label: value }))}
          value={sigText}
        />
      </div>
      <BrogliePanel fmt={fmt} sig={Number(sigText)} strings={strings} />
    </>
  );
}
